import { AbilityBuilder, createMongoAbility, type MongoAbility } from '@casl/ability';

import { isPermission, type Permission } from './permissions';

import type { SessionUser } from '@/core/auth/session-store';

/**
 * Flat permission model: each backend permission string is a CASL action over a single `'all'`
 * subject, so a guard reads `can('device.lock_full')`. Chosen over an action/subject split because the
 * contract permissions are flat dotted strings and gating is UX-only — a split adds a translation
 * layer that can drift, and it collides with CASL's `manage`-is-wildcard rule (`user.manage` is a
 * real permission that a split would make imply every other `user.*`). Full dotted strings are never
 * the bare `manage`/`all`, so nothing here is special-cased.
 */
export type AppAbility = MongoAbility<[Permission, 'all']>;

/** Rules in CASL's wire format (`packRules`-compatible), so a backend-resolved payload drops in. */
export type AbilityRules = Parameters<AppAbility['update']>[0];

/**
 * The only producer of rules: grants exactly the permissions present in the catalog. An unknown
 * string is ignored, never granted — the fail-closed default the whole layer rests on. Role→
 * permission resolution is deliberately absent; it belongs to claims hydration, not here, and a role
 * branch would be the place someone later encodes a grant the contract forbids.
 */
export function rulesFromPermissions(perms: Iterable<string>): AbilityRules {
  const { can, rules } = new AbilityBuilder<AppAbility>(createMongoAbility);

  for (const permission of perms) {
    if (isPermission(permission)) {
      can(permission, 'all');
    } else if (import.meta.env.DEV) {
      console.warn(`[authz] ignoring unknown permission: ${permission}`);
    }
  }

  return rules;
}

/** Adapter over the session's resolved permission set; a null/empty session denies everything. */
export function rulesFromSession(session: SessionUser | null): AbilityRules {
  return rulesFromPermissions(session?.permissions ?? []);
}

export function createAppAbility(rules: AbilityRules): AppAbility {
  return createMongoAbility<AppAbility>(rules);
}
